import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../../services/api';
import ConfirmationModal from '../ConfirmationModal';
import './GroceryListGenerator.css';

function GroceryListGenerator({ userId, mealPlan, onNavigateToTab }) {
  console.log('🛒 GroceryListGenerator rendered with onNavigateToTab:', typeof onNavigateToTab);
  const [groceryList, setGroceryList] = useState([]);
  const [groupedItems, setGroupedItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [shoppingMode, setShoppingMode] = useState(false);
  const [customItem, setCustomItem] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const categoryIcons = {
    'PRODUCE': '🥬',
    'DAIRY': '🥛',
    'MEAT': '🥩',
    'GRAINS': '🌾',
    'PANTRY': '🥫',
    'FROZEN': '🧊',
    'BEVERAGES': '🥤',
    'SNACKS': '🍿',
    'OTHER': '📦'
  };
  
  // Generate grocery list when meal plan changes
  useEffect(() => {
    console.log('🛒 GroceryListGenerator - mealPlan changed:', mealPlan);
    
    // Don't clear the list immediately - wait to see if we have valid data
    if (mealPlan && mealPlan.days && mealPlan.days.length > 0) {
      // Check if any day has actual meals (not just placeholder meals)
      const hasRealMeals = mealPlan.days.some(day => {
        const breakfast = day.breakfast && day.breakfast.name && day.breakfast.name !== 'Not assigned' && day.breakfast.id;
        const lunch = day.lunch && day.lunch.name && day.lunch.name !== 'Not assigned' && day.lunch.id;
        const dinner = day.dinner && day.dinner.name && day.dinner.name !== 'Not assigned' && day.dinner.id;
        const snack = day.snack && day.snack.name && day.snack.name !== 'Not assigned' && day.snack.id;
        return breakfast || lunch || dinner || snack;
      });
      
      if (hasRealMeals) {
        console.log('🛒 Generating grocery list from meal plan with', mealPlan.days.length, 'days');
        // Only regenerate if we have valid meals - don't clear existing list
        generateGroceryList();
      } else {
        console.log('🛒 Meal plan has no real meals, but keeping existing grocery list');
        // Don't clear - keep existing list if we had one
        // Only clear if we never had a list
        if (groceryList.length === 0) {
          setGroceryList([]);
          setGroupedItems({});
        }
      }
    } else {
      console.log('🛒 No meal plan or empty days');
      // Only clear if we never had items - preserve existing list
      if (groceryList.length === 0 && Object.keys(groupedItems).length === 0) {
        setGroceryList([]);
        setGroupedItems({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mealPlan]);
  
  
  const generateGroceryList = async () => {
    if (!mealPlan?.days || mealPlan.days.length === 0) {
      console.warn('⚠️ Cannot generate grocery list - no meal plan days');
      // Don't clear existing list if we're just missing data temporarily
      return;
    }
    
    // Filter out days with no real meals (exclude "Not assigned" meals)
    const daysWithMeals = mealPlan.days.filter(day => {
      const breakfast = day.breakfast && day.breakfast.name && day.breakfast.name !== 'Not assigned' && day.breakfast.id;
      const lunch = day.lunch && day.lunch.name && day.lunch.name !== 'Not assigned' && day.lunch.id;
      const dinner = day.dinner && day.dinner.name && day.dinner.name !== 'Not assigned' && day.dinner.id;
      const snack = day.snack && day.snack.name && day.snack.name !== 'Not assigned' && day.snack.id;
      return breakfast || lunch || dinner || snack;
    });
    
    if (daysWithMeals.length === 0) {
      console.warn('⚠️ No days with real meals found in meal plan');
      setGroceryList([]);
      setGroupedItems({});
      return;
    }
    
    console.log('🛒 Generating grocery list for', daysWithMeals.length, 'days with meals');
    console.log('   Days:', daysWithMeals.map(d => ({
      date: d.date instanceof Date ? d.date.toISOString().split('T')[0] : d.date,
      breakfast: d.breakfast?.name,
      lunch: d.lunch?.name,
      dinner: d.dinner?.name,
      snack: d.snack?.name
    })));
    
    try {
      // Convert Date objects to ISO strings for API, and ensure we only send days with real meals
      const daysForApi = daysWithMeals.map(day => {
        const apiDay = { ...day };
        // Convert date to string if it's a Date object
        if (apiDay.date instanceof Date) {
          apiDay.date = apiDay.date.toISOString().split('T')[0];
        } else if (apiDay.date && typeof apiDay.date === 'object' && apiDay.date.toISOString) {
          apiDay.date = apiDay.date.toISOString().split('T')[0];
        }
        return apiDay;
      });
      
      console.log('🛒 Sending to API:', daysForApi.length, 'days');
      console.log('🛒 Sample day data:', JSON.stringify(daysForApi[0], null, 2));
      
      // Log meal data to check if ingredients are present
      daysForApi.forEach((day, idx) => {
        ['breakfast', 'lunch', 'dinner', 'snack'].forEach(mealType => {
          const meal = day[mealType];
          if (meal) {
            const hasIngredients = meal.ingredients && Array.isArray(meal.ingredients) && meal.ingredients.length > 0;
            console.log(`   Day ${idx + 1} ${mealType}: ${meal.name} - Ingredients: ${hasIngredients ? meal.ingredients.length : 'MISSING'}`);
          }
        });
      });
      
      const response = await api.generateGroceryList(daysForApi);
      
      // apiClient interceptor already extracts data from {success: true, data: [...]}
      // So response is already the array, not response.data
      const items = Array.isArray(response) ? response : (response?.data || []);
      
      console.log('🛒 API Response type:', typeof response, 'Is array:', Array.isArray(response));
      console.log('🛒 Generated', items.length, 'grocery items from API');
      if (items.length > 0) {
        console.log('🛒 Sample items:', items.slice(0, 3));
        console.log('🛒 Full items array:', items);
      } else {
        console.warn('⚠️ API returned no grocery items');
        console.warn('⚠️ Response was:', response);
      }
      
      if (items.length === 0) {
        console.warn('⚠️ No grocery items to display');
        setGroceryList([]);
        setGroupedItems({});
        return;
      }
      
      const grouped = items.reduce((acc, item) => {
        const category = item.category || 'OTHER';
        if (!acc[category]) acc[category] = [];
        acc[category].push({ ...item, checked: false, id: item.id || `${category}-${item.name}` });
        return acc;
      }, {});
      
      const groceryListItems = items.map(item => ({ ...item, checked: false, id: item.id || `${item.category || 'OTHER'}-${item.name}` }));
      
      console.log('🛒 Setting grocery list state:', {
        itemsCount: groceryListItems.length,
        categories: Object.keys(grouped),
        sampleItem: groceryListItems[0]
      });
      
      setGroceryList(groceryListItems);
      setGroupedItems(grouped);
      
      console.log('✅ Grocery list state updated:', { 
        totalItems: groceryListItems.length, 
        categories: Object.keys(grouped),
        categoryCounts: Object.entries(grouped).map(([cat, items]) => `${cat}: ${items.length}`)
      });
    } catch (error) {
      console.error('❌ Error generating grocery list:', error);
      console.error('   Error details:', error.response?.data || error.message);
      setGroceryList([]);
      setGroupedItems({});
    }
  };
  
  const toggleItem = (itemId) => {
    setGroceryList(prev => prev.map(item => 
      item.id === itemId ? { ...item, checked: !item.checked } : item
    ));
    
    setGroupedItems(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(category => {
        updated[category] = updated[category].map(item =>
          item.id === itemId ? { ...item, checked: !item.checked } : item
        );
      });
      return updated;
    });
  };
  
  const addCustomItem = () => {
    if (!customItem.trim()) return;
    
    const newItem = {
      id: `custom-${Date.now()}`,
      name: customItem.trim(),
      quantity: '1',
      category: 'OTHER',
      checked: false,
      custom: true
    };
    
    setGroceryList(prev => [...prev, newItem]);
    setGroupedItems(prev => ({
      ...prev,
      OTHER: [...(prev.OTHER || []), newItem]
    }));
    setCustomItem('');
    setShowAddItem(false);
  };
  
  const deleteItem = (itemId) => {
    setGroceryList(prev => prev.filter(item => item.id !== itemId));
    setGroupedItems(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(category => {
        updated[category] = updated[category].filter(item => item.id !== itemId);
      });
      return updated;
    });
  };
  
  const clearChecked = () => {
    setGroceryList(prev => prev.map(item => ({ ...item, checked: false })));
    setGroupedItems(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(category => {
        updated[category] = updated[category].map(item => ({ ...item, checked: false }));
      });
      return updated;
    });
  };
  
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const exportToEmail = () => {
    const uncheckedItems = groceryList.filter(item => !item.checked);
    if (uncheckedItems.length === 0) {
      setFeedbackModal({
        isOpen: true,
        title: 'Nothing to export',
        message: 'All items are checked! Nothing to export.',
        type: 'info'
      });
      return;
    }
    
    const grouped = {};
    uncheckedItems.forEach(item => {
      const cat = item.category || 'OTHER';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });
    
    let itemsText = '🛒 MY GROCERY LIST\n\n';
    Object.entries(grouped).forEach(([category, items]) => {
      itemsText += `${categoryIcons[category] || '📦'} ${category}\n`;
      items.forEach(item => {
        itemsText += `  ✓ ${item.name} (${item.quantity || '1'})\n`;
      });
      itemsText += '\n';
    });
    
    const subject = encodeURIComponent('My Grocery List');
    const body = encodeURIComponent(itemsText);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  
  const exportToText = () => {
    const uncheckedItems = groceryList.filter(item => !item.checked);
    if (uncheckedItems.length === 0) {
      setFeedbackModal({
        isOpen: true,
        title: 'Nothing to export',
        message: 'All items are checked! Nothing to export.',
        type: 'info'
      });
      return;
    }
    
    const grouped = {};
    uncheckedItems.forEach(item => {
      const cat = item.category || 'OTHER';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });
    
    let content = '🛒 MY GROCERY LIST\n\n';
    Object.entries(grouped).forEach(([category, items]) => {
      content += `${categoryIcons[category] || '📦'} ${category}\n`;
      items.forEach(item => {
        content += `  • ${item.name} (${item.quantity || '1'})\n`;
      });
      content += '\n';
    });
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `grocery-list-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  const shareList = async () => {
    const uncheckedItems = groceryList.filter(item => !item.checked);
    if (uncheckedItems.length === 0) {
      setFeedbackModal({
        isOpen: true,
        title: 'Nothing to share',
        message: 'All items are checked! Nothing to share.',
        type: 'info'
      });
      return;
    }
    
    const text = uncheckedItems.map(item => `• ${item.name} (${item.quantity || '1'})`).join('\n');
    const shareText = `🛒 My Grocery List:\n\n${text}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Grocery List',
          text: shareText
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(shareText);
      setFeedbackModal({
        isOpen: true,
        title: 'Copied to clipboard',
        message: 'Grocery list copied to clipboard!',
        type: 'success'
      });
    }
  };
  
  const filteredItems = searchQuery 
    ? groceryList.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : groceryList;
  
  const filteredGrouped = searchQuery 
    ? filteredItems.reduce((acc, item) => {
        const category = item.category || 'OTHER';
        if (!acc[category]) acc[category] = [];
        acc[category].push(item);
        return acc;
      }, {})
    : groupedItems;
  
  const categories = Object.keys(filteredGrouped).filter(cat => 
    selectedCategory === 'all' || cat === selectedCategory
  );
  
  const totalItems = groceryList.length;
  const checkedItems = groceryList.filter(item => item.checked).length;
  const progress = totalItems > 0 ? (checkedItems / totalItems) * 100 : 0;
  
  // Debug logging on render
  useEffect(() => {
    console.log('🛒 GroceryListGenerator render state:', {
      totalItems,
      groceryListLength: groceryList.length,
      groupedItemsKeys: Object.keys(groupedItems),
      groupedItemsCounts: Object.entries(groupedItems).map(([cat, items]) => `${cat}: ${items.length}`),
      hasMealPlan: !!mealPlan,
      mealPlanDays: mealPlan?.days?.length
    });
  }, [groceryList, groupedItems, totalItems, mealPlan]);

  return (
    <div className="grocery-list-generator-modern">
      <div className="grocery-header-modern">
        <motion.div
          className="grocery-icon-wrapper"
          animate={{ rotate: [0, -10, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
        >
          <div className="grocery-icon">🛒</div>
        </motion.div>
        <div className="grocery-header-text">
          <h2>Smart Grocery List</h2>
          <p>Automatically generated from your meal plan, organized by store sections for efficient shopping</p>
          <div className="header-help">
            <span className="help-icon">💡</span>
            <span className="help-text">Click items to check them off as you shop. Use Shopping Mode to hide checked items.</span>
          </div>
        </div>
      </div>
      
      {totalItems > 0 && (
        <motion.div 
          className="grocery-progress-section"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="progress-header">
            <div className="progress-info">
              <span className="progress-text">
                {checkedItems} of {totalItems} items checked
              </span>
              <span className="progress-hint">Track your shopping progress in real-time</span>
            </div>
            <span className="progress-percentage">{Math.round(progress)}%</span>
          </div>
          <div className="progress-bar-container">
            <motion.div
              className="progress-bar-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          {progress >= 100 && (
            <div className="progress-complete-message">
              🎉 All items checked! Great job completing your shopping list!
            </div>
          )}
        </motion.div>
      )}
      
      <div className="grocery-controls">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button 
              className="clear-search"
              onClick={() => setSearchQuery('')}
            >
              ✕
            </button>
          )}
        </div>
        
        <div className="control-buttons">
          <div className="control-button-group">
            <motion.button
              className={`mode-toggle ${shoppingMode ? 'active' : ''}`}
              onClick={() => setShoppingMode(!shoppingMode)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title={shoppingMode ? 'Switch to Planning Mode to see all items' : 'Switch to Shopping Mode to hide checked items'}
            >
              {shoppingMode ? '🛍️ Shopping Mode' : '📝 Planning Mode'}
            </motion.button>
            <span className="button-hint">
              {shoppingMode 
                ? '✓ Checked items are hidden - perfect for shopping!'
                : 'All items visible - great for planning your trip'}
            </span>
          </div>
          
          <div className="control-button-group">
            <motion.button
              className="add-item-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('➕ Add Item button clicked');
                setShowAddItem(!showAddItem);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Add custom items to your grocery list"
              type="button"
            >
              ➕ Add Item
            </motion.button>
            <span className="button-hint">Add items not in your meal plan</span>
          </div>
        </div>
      </div>
      
      <AnimatePresence>
        {showAddItem && (
          <motion.div
            className="add-item-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <input
              type="text"
              placeholder="Item name (e.g., Organic Bananas)"
              value={customItem}
              onChange={(e) => setCustomItem(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCustomItem()}
              className="custom-item-input"
              autoFocus
            />
            <div className="add-item-actions">
              <motion.button
                className="save-item-btn"
                onClick={addCustomItem}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ✓ Add
              </motion.button>
              <motion.button
                className="cancel-item-btn"
                onClick={() => {
                  setShowAddItem(false);
                  setCustomItem('');
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ✕ Cancel
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {totalItems > 0 && (
        <div className="category-filter-section">
          <div className="filter-header">
            <h3>Filter by Category</h3>
            <span className="filter-hint">Click a category to see items from that section only</span>
          </div>
          <div className="category-filter">
            <motion.button
              className={`category-chip ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Show all items from all categories"
            >
              All ({totalItems})
            </motion.button>
            {Object.keys(groupedItems).map(category => (
              <motion.button
                key={category}
                className={`category-chip ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title={`Show only ${category.toLowerCase()} items - typically found in the ${category.toLowerCase()} section of the store`}
              >
                {categoryIcons[category] || '📦'} {category} ({groupedItems[category].length})
              </motion.button>
            ))}
          </div>
        </div>
      )}
      
      {totalItems === 0 ? (
        <motion.div
          className="empty-grocery-state"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="empty-icon">🛒</div>
          <h3>Your grocery list is empty</h3>
          <p>Generate a meal plan first, then your grocery list will automatically appear here!</p>
          
          <div className="empty-instructions">
            <h4>How to get started:</h4>
            <div className="instructions-actions">
              <motion.button
                className="instruction-action-button weekly-plan-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🔄 Clicked Weekly Plan button, onNavigateToTab:', typeof onNavigateToTab);
                  if (onNavigateToTab && typeof onNavigateToTab === 'function') {
                    onNavigateToTab('weekly');
                  } else {
                    console.error('❌ onNavigateToTab is not a function:', onNavigateToTab);
                  }
                }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                type="button"
              >
                <span className="action-icon">📅</span>
                <div className="action-content">
                  <strong>Go to Weekly Plan</strong>
                  <span className="action-description">Generate a 7-day meal plan</span>
                </div>
              </motion.button>
              
              <motion.button
                className="instruction-action-button daily-builder-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🔄 Clicked Daily Builder button, onNavigateToTab:', typeof onNavigateToTab);
                  if (onNavigateToTab && typeof onNavigateToTab === 'function') {
                    onNavigateToTab('builder');
                  } else {
                    console.error('❌ onNavigateToTab is not a function:', onNavigateToTab);
                  }
                }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                type="button"
              >
                <span className="action-icon">🍽️</span>
                <div className="action-content">
                  <strong>Go to Daily Builder</strong>
                  <span className="action-description">Add meals for today</span>
                </div>
              </motion.button>
              
              <motion.button
                className="instruction-action-button add-item-btn-instruction"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('➕ Clicked Add Item button');
                  setShowAddItem(true);
                }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                type="button"
              >
                <span className="action-icon">➕</span>
                <div className="action-content">
                  <strong>Add Custom Item</strong>
                  <span className="action-description">Add items not in meal plan</span>
                </div>
              </motion.button>
            </div>
            
            <div className="instructions-info">
              <p>💡 Once you have meals, come back here to see your organized grocery list!</p>
              <p>💡 Your grocery list is automatically generated from all the ingredients in your meal plan!</p>
            </div>
          </div>
        </motion.div>
      ) : (
        <div className="grocery-categories-modern">
          <AnimatePresence>
            {categories.map((category) => {
              const items = filteredGrouped[category].filter(item => 
                !shoppingMode || !item.checked
              );
              
              if (items.length === 0) return null;
              
              return (
                <motion.div
                  key={category}
                  className="grocery-category-modern"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <div className="category-header">
                    <div className="category-header-main">
                      <h3>
                        <span className="category-icon">{categoryIcons[category] || '📦'}</span>
                        {category}
                      </h3>
                      <span className="category-description">
                        {category === 'PRODUCE' && 'Fresh fruits and vegetables'}
                        {category === 'DAIRY' && 'Milk, cheese, yogurt, and dairy products'}
                        {category === 'MEAT' && 'Fresh meat, poultry, and seafood'}
                        {category === 'GRAINS' && 'Bread, rice, pasta, and grains'}
                        {category === 'PANTRY' && 'Canned goods, spices, and dry goods'}
                        {category === 'FROZEN' && 'Frozen fruits, vegetables, and meals'}
                        {category === 'BEVERAGES' && 'Drinks, juices, and beverages'}
                        {category === 'SNACKS' && 'Snacks, crackers, and treats'}
                        {category === 'OTHER' && 'Miscellaneous items'}
                      </span>
                    </div>
                    <span className="category-count">{items.length} item{items.length !== 1 ? 's' : ''}</span>
                  </div>
                  
                  <div className="grocery-items-modern">
                    {items.map((item, index) => {
                      const itemId = item.id;
                      const isChecked = item.checked || false;
                      
                      return (
                        <motion.div
                          key={itemId}
                          className={`grocery-item-modern ${isChecked ? 'checked' : ''} ${shoppingMode ? 'shopping-mode' : ''}`}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ scale: 1.02, x: 5 }}
                        >
                          <div 
                            className="item-checkbox-wrapper"
                            onClick={() => toggleItem(itemId)}
                          >
                            <motion.div
                              className={`item-checkbox ${isChecked ? 'checked' : ''}`}
                              animate={{ scale: isChecked ? [1, 1.2, 1] : 1 }}
                              transition={{ duration: 0.3 }}
                            >
                              {isChecked && '✓'}
                            </motion.div>
                          </div>
                          
                          <div className="item-content">
                            <div className="item-main">
                              <span className={`item-name ${isChecked ? 'checked' : ''}`}>
                                {item.name}
                              </span>
                              {item.notes && (
                                <span className="item-notes" title={item.notes}>
                                  💬 {item.notes}
                                </span>
                              )}
                            </div>
                            <div className="item-quantity-wrapper">
                              <span className="item-quantity-label">Qty:</span>
                              <span className="item-quantity">{item.quantity || '1'}</span>
                            </div>
                          </div>
                          
                          {item.custom && (
                            <motion.button
                              className="delete-item-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteItem(itemId);
                              }}
                              whileHover={{ scale: 1.2 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              🗑️
                            </motion.button>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
          
          {checkedItems > 0 && (
            <motion.div
              className="checked-summary"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="summary-content">
                <span className="summary-icon">✓</span>
                <span>{checkedItems} item{checkedItems !== 1 ? 's' : ''} checked off</span>
              </div>
              <motion.button
                className="clear-checked-btn"
                onClick={clearChecked}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Clear All
              </motion.button>
            </motion.div>
          )}
        </div>
      )}
      
      {totalItems > 0 && (
        <div className="grocery-actions-section">
          <div className="actions-header">
            <h3>Export & Share</h3>
            <p className="actions-description">Take your list with you or share it with family members</p>
          </div>
          <div className="grocery-actions-modern">
            <motion.button
              className="action-btn email-btn"
              onClick={exportToEmail}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              title="Email your grocery list to yourself or family members"
            >
              📧 Email List
            </motion.button>
            <motion.button
              className="action-btn download-btn"
              onClick={exportToText}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              title="Download as a text file to save or print"
            >
              📥 Download
            </motion.button>
            <motion.button
              className="action-btn share-btn"
              onClick={shareList}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              title="Share via your device's share options or copy to clipboard"
            >
              🔗 Share
            </motion.button>
          </div>
          <div className="export-hint">
            <span className="hint-icon">💡</span>
            <span className="hint-text">Only unchecked items will be exported - perfect for sharing what you still need to buy!</span>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={feedbackModal.isOpen}
        onClose={() => setFeedbackModal({ isOpen: false, title: '', message: '', type: 'info' })}
        onConfirm={() => {}}
        title={feedbackModal.title}
        message={feedbackModal.message}
        type={feedbackModal.type}
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

export default GroceryListGenerator;
